import React, {
  useRef, useState, useCallback, useEffect,
} from 'react';
import { Modal } from '@/pages/common';
import { Button, ProgressBar } from 'gatorcomponents';
import IconCamera from '../../IconCamera';
import CropImageModal from '../CropImageModal';
import * as Styles from './ProfileImageModal.styles';
import { validateProfileImage } from '@/utils/imageValidation';
import useLocale from '@/hooks/useLocale';

const ProfileImageModal = ({
  isOpen,
  onClose,
  onImageReady,
  isUploading: externalUploading = false,
}) => {
  const { linkNaBio: locale } = useLocale();
  const modalLocale = locale.profileImageModal;
  const inputRef = useRef(null);
  const [errorKey, setErrorKey] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [waitingUploadComplete, setWaitingUploadComplete] = useState(false);

  const reset = useCallback(() => {
    setIsUploading(false);
    setProgress(0);
    setPreview(null);
    setFile(null);
    setErrorKey(null);
    setShowCropper(false);
    setIsSaving(false);
    setWaitingUploadComplete(false);
  }, []);

  useEffect(() => {
    if (waitingUploadComplete && !externalUploading) {
      reset();
      onClose();
    }
  }, [externalUploading, waitingUploadComplete, onClose, reset]);

  const handleFileSelect = useCallback(async (fileObj) => {
    if (!fileObj) return;
    const validation = await validateProfileImage(fileObj);
    if (!validation.valid) {
      setErrorKey(validation.errors[0]);
      return;
    }

    setIsUploading(true);
    setProgress(15);

    const reader = new FileReader();
    reader.onloadend = () => {
      setProgress(80);
      setPreview(reader.result);
      setErrorKey(null);
      setFile(fileObj);
      setIsUploading(false);
      setProgress(100);
      setShowCropper(true);
    };
    reader.readAsDataURL(fileObj);
  }, []);

  const handleInputChange = (e) => {
    const sel = e.target.files && e.target.files[0];
    handleFileSelect(sel);
    e.target.value = '';
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const sel = e.dataTransfer.files && e.dataTransfer.files[0];
    handleFileSelect(sel);
  }, [handleFileSelect]);

  const handleDrag = (e) => {
    e.preventDefault();
  };

  const startPick = () => inputRef.current && inputRef.current.click();


  const cancelDuringUpload = () => {
    reset();
  };

  const handleCropComplete = async (croppedImageDataUrl) => {
    setIsSaving(true);

    try {
      const res = await fetch(croppedImageDataUrl);
      const blob = await res.blob();
      const croppedFile = new File([blob], file.name, {
        type: 'image/jpeg',
      });

      onImageReady(croppedFile, croppedImageDataUrl);
      setWaitingUploadComplete(true);
    } catch (error) {
      setIsSaving(false);
    }
  };

  const handleCancelCrop = () => {
    reset();
  };

  if (showCropper && preview && !isUploading) {
    return (
      <CropImageModal
        isOpen={showCropper}
        imageSrc={preview}
        onCropComplete={handleCropComplete}
        onCancel={handleCancelCrop}
        locale={modalLocale}
        isSaving={isSaving}
      />
    );
  }

  return isOpen ? (
    <Modal maxWidth={640} onClose={() => onClose()} variant="regular" disablePadding>
      <Styles.Title>{modalLocale.title}</Styles.Title>

      {!preview && (
        <Styles.DragArea
          onDrop={handleDrop}
          onDragOver={handleDrag}
          role="button"
          htmlFor="profile-modal-input"
          as="label"
        >
          <Styles.CameraIconWrapper>
            <IconCamera />
          </Styles.CameraIconWrapper>
          <Styles.DragText>{modalLocale.dragTitle}</Styles.DragText>
          <Styles.Recommendation>{modalLocale.recommendation}</Styles.Recommendation>
          <Button label={modalLocale.buttonPick} onClick={startPick} />
          <Styles.HiddenInput
            id="profile-modal-input"
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={handleInputChange}
          />
          {errorKey === 'profilePictureInvalidSize' && (
            <Styles.ErrorText>
              {modalLocale.errorTooLarge}
            </Styles.ErrorText>
          )}
          {errorKey === 'profilePictureInvalidType' && (
            <Styles.ErrorText>
              {modalLocale.errorInvalidType}
            </Styles.ErrorText>
          )}
          {errorKey === 'profilePictureInvalidDimensionsTooSmall' && (
            <Styles.ErrorText>
              {modalLocale.errorTooSmall}
            </Styles.ErrorText>
          )}
        </Styles.DragArea>
      )}

      {isUploading && (
        <Styles.Uploading>
          <Styles.UploadingName>{modalLocale.uploading}</Styles.UploadingName>
          <ProgressBar progress={progress} />
          <Styles.CancelWrapper>
            <Button label={modalLocale.cancelUpload} variant="secondary" onClick={cancelDuringUpload} />
          </Styles.CancelWrapper>
        </Styles.Uploading>
      )}
    </Modal>
  ) : null;
};

export default ProfileImageModal;
